import {
  getElementLabel,
  isOptimizationElement,
} from '@packages/utilities/platform';
import {Component} from '@packages/systems/wfdl';
import {
  selectElementFromSelectedNodeNativeId,
  selectComponents,
  selectCurrentComponent,
  hasSelectedComponentInstance,
  selectSelectedElement,
  selectCurrentUserComponentId,
} from '@packages/utilities/platform-selectors';
import {
  componentInstanceCountsSummary as getComponentInstanceCountsSummary,
  componentInstanceCountsByPage as getComponentInstanceCountsByPage,
} from '@packages/systems/user-components/utils';
import {type DesignerStateType} from '@packages/systems/core/records/DesignerState';
import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';

import {
  DESIGNER_USER_TYPES,
  DESIGNER_SITE_MODES,
  type DesignerUserType,
  type DesignerSiteMode,
  TOP_BAR_HEIGHT,
  TOP_BAR_HEIGHT_COMFORTABLE,
  leftSidebarWidth,
  leftSidebarWidthComfortable,
} from '@packages/systems/design-system/constants/DesignerUI';
import {DESIGNER_WORKFLOWS} from '@packages/systems/designer/workflows';

import type {DesignerStores} from '@packages/systems/designer/flux';
import {createSelector} from 'reselect';

import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {Breakpoint} from '@packages/systems/style/media-queries';
import {
  getActivePath,
  getActiveRoot,
  getActivePanelName,
  getPanels,
} from '@packages/systems/panels';
import {
  PAGES_PANEL,
  PAGE_SETTINGS_PANEL,
  NAVIGATOR_PANEL,
} from '@packages/systems/designer/panels';
import {
  selectCurrentExperienceId,
  selectCurrentVariationId,
  selectCurrentVariationIsBase,
  selectIsInOptimizeMode,
} from '@packages/systems/optimization/store';
import {isEditingComponentInContext} from '@packages/systems/component-canvas/designer';
import {OpenCanvas} from '@packages/systems/designer/open-canvas-config';
import {selectIsCodeComponentCanvas} from '@packages/systems/component-canvas/universal';
import {
  selectPageState,
  selectCurrentPageId,
} from '@packages/systems/designer/page';
import {SiteDataStateType} from '@packages/systems/designer/site-data';
import {resolveAppearanceThemeForSurface} from '@packages/shared/logic/globalInterfaceTheme';
import type {AppearanceSettings} from '@packages/shared/types/appearanceSettings';
import type {
  DarkThemeStyle,
  GlobalThemeSelection,
  OSThemePreference,
  ThemeSurfaceOverrides,
} from '@packages/shared/types/globalInterfaceTheme';

export const selectIsPagesPanelActive = (state: DesignerStores) =>
  getActivePanelName(state.PanelsStore, 'left') === PAGES_PANEL;

export const selectCanvasRulerState = (state: DesignerStores) =>
  state.UiStore.leftSidebarTips?.rulers || 'no-ruler';

// Should be safe since `getActiveRoot` doesn't return a new object:
// eslint-disable-next-line webflow/selector-memoization
export const selectActiveLeftPanel = (state: DesignerStores) =>
  getActiveRoot(state.PanelsStore, 'left');

// Should be safe since `getActivePanelName` doesn't return an object:
// eslint-disable-next-line webflow/selector-memoization
export const selectActivePanelName = (state: DesignerStores) =>
  getActivePanelName(state.PanelsStore, 'left');

export const selectIsPagesSettingPanelActive = (state: DesignerStores) =>
  getActivePanelName(state.PanelsStore, 'left') === PAGE_SETTINGS_PANEL;

/**
 * Whether any panel on the active left path declares
 * `metadata.suppressesPageContext` — a full-canvas takeover (Ads, Brand,
 * Activate), where the customer is no longer working on a page,
 * so page-scoped top-bar chrome must not render. Generic by design: a new
 * takeover panel opts in with one metadata flag rather than every consumer
 * growing another panel name to compare against. Reads the whole active path
 * rather than just the deepest panel, so the flag means "this panel or anything
 * under it" — none of today's takeovers has children, but `getActivePanelName`
 * would silently stop suppressing the moment one gained a child panel.
 */
export const selectIsPageContextSuppressedByActivePanel = createSelector(
  (state: DesignerStores) => getActivePath(state.PanelsStore, 'left'),
  (state: DesignerStores) => getPanels(state.PanelsStore).all,
  (activePath, panelsByName): boolean =>
    (activePath ?? []).some(
      (panelName) =>
        panelsByName[panelName]?.metadata.suppressesPageContext === true
    )
);

/**
 * Checks if the canvas is the main focus by evaluating the current UI state.
 * It verifies that neither preview, annotations, nor view modes are active,
 * the left panel is inactive or set to the navigator, the AI chat modal is not visible, and the export panel is not shown.
 */
export const selectIsCanvasFocused = (state: DesignerStores): boolean => {
  const leftPanel = getActiveRoot(state.PanelsStore, 'left');
  const isAnnotationsMode = getIsAnnotationsWorkflow(state.DesignerStore);
  const isAnalyzeMode = getIsAnalyzeWorkflow(state.DesignerStore);
  const isPreviewMode = getIsPreviewWorkflow(state.DesignerStore);
  const isViewSiteMode = getIsViewSiteMode(state.DesignerStore);

  return (
    !isPreviewMode &&
    !isAnnotationsMode &&
    !isViewSiteMode &&
    !isAnalyzeMode &&
    (leftPanel === null || leftPanel === NAVIGATOR_PANEL) &&
    !state.ExportStore.visible
  );
};

type OrionRootState = DesignerStores['OrionRootStore'];

const selectOrionMode = ({OrionRootStore}: {OrionRootStore: OrionRootState}) =>
  OrionRootStore.OrionAppState.orionMode;

const selectOrionCreateModeLoadingStatuses = ({
  OrionRootStore,
}: {
  OrionRootStore: OrionRootState;
}) => OrionRootStore.OrionAppState.orionCreateModeLoadingStatuses;

const selectOrionStatus = ({
  OrionRootStore,
}: {
  OrionRootStore: OrionRootState;
}) => OrionRootStore.OrionAppState.orionStatus;

export const selectIsCanvasResizing = (state: DesignerStores) =>
  state.CanvasResizeStore.isResizing;

export const selectWorkspaceCanvasWidth = (state: DesignerStores) =>
  state.CanvasResizeStore.workspaceDimensions.width;

export const selectCanvasWidth = (state: DesignerStores) =>
  state.CanvasResizeStore.width;

export const selectCanvasHeight = (state: DesignerStores) =>
  state.CanvasResizeStore.height;

/**
 * Returns the effective canvas scale. In Open Canvas the iframe has no CSS
 * transform, so CanvasResizeStore.scale (which can hold a stale page-canvas
 * value) must not be applied — this selector returns 1 in that case.
 */
export const selectEffectiveCanvasScale = (state: DesignerStores): number =>
  OpenCanvas.selectIsOpenCanvasActive(state)
    ? 1
    : state.CanvasResizeStore.scale;

export const selectCanvasBarPosition = (state: DesignerStores) =>
  state.UiStore.canvasBarPosition;

export const getDesignerUserType: (
  arg1: DesignerStateType
) => DesignerUserType = (state) => state.userType;

export const getDesignerWorkflow = (state: DesignerStateType) => state.workflow;
export const selectDesignerWorkflow = (state: DesignerStores) =>
  state.DesignerStore.workflow;

export const getDesignerSiteMode: (
  arg1: DesignerStateType
) => DesignerSiteMode = (state) => state.siteMode;

// Should be safe since `getDesignerSiteMode` doesn't return an object:
// eslint-disable-next-line webflow/selector-memoization
export const selectDesignerSiteMode = (state: DesignerStores) =>
  getDesignerSiteMode(state.DesignerStore);

// WORKFLOWS GETTERS AND SELECTORS
export const getIsPreviewWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.PREVIEW;

const getIsCodeModeWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.CODE_MODE;

export const selectIsCodeModeWorkflow = (state: DesignerStores) =>
  getIsCodeModeWorkflow(state.DesignerStore);

const getIsAgentsModeWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.AGENTS;

export const selectIsAgentsModeWorkflow = (state: DesignerStores) =>
  getIsAgentsModeWorkflow(state.DesignerStore);

export const selectIsPreviewWorkflow = (state: DesignerStores) =>
  getIsPreviewWorkflow(state.DesignerStore);

export const getIsAnnotationsWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.COMMENT;

export const selectIsAnnotationsWorkflow = (state: DesignerStores) =>
  getDesignerWorkflow(state.DesignerStore) === DESIGNER_WORKFLOWS.COMMENT;

export const getIsAnalyzeWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.ANALYZE_VISUAL_VIEW;

export const selectIsAnalyzeWorkflow = (state: DesignerStores) =>
  getIsAnalyzeWorkflow(state.DesignerStore);

export const getIsOptimizationClickGoalWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.OPTIMIZATION_CLICK_GOAL;

export const selectIsOptimizationClickGoalWorkflow = (state: DesignerStores) =>
  getIsOptimizationClickGoalWorkflow(state.DesignerStore);

export const getIsOptimizationWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.OPTIMIZATION;

export const selectIsOptimizationWorkflow = (state: DesignerStores) =>
  getIsOptimizationWorkflow(state.DesignerStore);

export const getIsDesignerCanvasWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.CANVAS;

export const selectIsDesignerCanvasWorkflow = (state: DesignerStores) =>
  getIsDesignerCanvasWorkflow(state.DesignerStore);

export const getIsLogicFlowsWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.LOGIC_FLOWS;

export const getIsDynamicDataWorkflow = (state: DesignerStateType) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.DYNAMIC_DATA;

export const selectIsDynamicDataWorkflow = (state: DesignerStores) =>
  getIsDynamicDataWorkflow(state.DesignerStore);

/**
 * Left Panel visibility
 *
 * Returns whether the `LeftSidebar` in the Designer is visible based on the current workflow
 */
export const selectIsLeftSidebarVisible = (state: {
  DesignerStore: DesignerStateType;
  OrionRootStore: OrionRootState;
}) => {
  const workflow = getDesignerWorkflow(state.DesignerStore);
  const orionMode = selectOrionMode(state);
  const orionStatus = selectOrionStatus(state);
  const orionCreateModeLoadingStatuses =
    selectOrionCreateModeLoadingStatuses(state);

  switch (workflow) {
    case DESIGNER_WORKFLOWS.CANVAS:
      return true;
    case DESIGNER_WORKFLOWS.CODE_MODE:
      if (
        orionMode === 'create' ||
        orionCreateModeLoadingStatuses?.includes(orionStatus)
      ) {
        return false;
      }
      return true;
    case DESIGNER_WORKFLOWS.PREVIEW:
      return false;
    case DESIGNER_WORKFLOWS.DYNAMIC_DATA:
      return false;
    case DESIGNER_WORKFLOWS.COMMENT:
      return false;
    case DESIGNER_WORKFLOWS.OPTIMIZATION:
      return false;
    case DESIGNER_WORKFLOWS.OPTIMIZATION_CLICK_GOAL:
      return false;
    case DESIGNER_WORKFLOWS.OPTIMIZATION_REDIRECT_TEST:
      return false;
    case DESIGNER_WORKFLOWS.LOGIC_FLOWS:
      return true;
    case DESIGNER_WORKFLOWS.ANALYZE_VISUAL_VIEW:
      return true;
    case DESIGNER_WORKFLOWS.AGENTS:
      return true;
    case DESIGNER_WORKFLOWS.CAMPAIGNS_HOME:
      return false;
    // default:
    // NOTE: The default case is intentionally left out unimplemented so that TS can
    // enforce adding a new explicit case when a new `WorkflowType` is defined.
  }
};

/**
 * Designer spacing mode options.
 * - 'default': Standard spacing used for most site types
 * - 'comfortable': More generous spacing and larger UI elements, used for campaign sites
 */
export type DesignerSpacing = 'default' | 'comfortable';

/**
 * Determines the spacing mode for the designer UI.
 * Comfortable mode provides more generous spacing and larger UI elements throughout the designer.
 * Currently based on site kind (campaign sites use comfortable mode).
 * In the future, this could be controlled by a user preference setting.
 */
export const selectDesignerSpacing = (state: {
  SiteDataStore: SiteDataStateType;
}): DesignerSpacing => {
  const siteKind = state.SiteDataStore.kind ?? 'webflow';
  return siteKind === 'campaign' ? 'comfortable' : 'default';
};

/**
 * Returns whether the designer is in comfortable spacing mode (campaign sites).
 */
export const selectIsComfortableSpacing = (state: {
  SiteDataStore: SiteDataStateType;
}): boolean => selectDesignerSpacing(state) === 'comfortable';

/**
 * Returns the left sidebar width based on the current designer spacing mode.
 * - Returns 80px for 'comfortable' spacing (campaign sites)
 * - Returns 40px for 'default' spacing (all other site types)
 *
 * This selector centralizes the logic for determining left sidebar width,
 * eliminating the need for components to conditionally select between
 * leftSidebarWidth and leftSidebarWidthComfortable constants.
 */
export const selectLeftSidebarWidth = (state: {
  SiteDataStore: SiteDataStateType;
}): number => {
  const spacing = selectDesignerSpacing(state);
  return spacing === 'comfortable'
    ? leftSidebarWidthComfortable
    : leftSidebarWidth;
};

export const selectTopBarHeight = (state: {
  SiteDataStore: SiteDataStateType;
}): number => {
  const spacing = selectDesignerSpacing(state);
  return spacing === 'comfortable'
    ? TOP_BAR_HEIGHT_COMFORTABLE
    : TOP_BAR_HEIGHT;
};

/**
 * Returns whether the workflow includes either a full screen canvas (such as preview workflow)
 * or a panel which takes over the full screen (such as dynamic data)
 */
export const selectIsFullWidthWorkflow = (state: {
  DesignerStore: DesignerStateType;
}): boolean => {
  const workflow = getDesignerWorkflow(state.DesignerStore);
  switch (workflow) {
    case DESIGNER_WORKFLOWS.PREVIEW:
    case DESIGNER_WORKFLOWS.DYNAMIC_DATA:
    case DESIGNER_WORKFLOWS.OPTIMIZATION:
    case DESIGNER_WORKFLOWS.OPTIMIZATION_CLICK_GOAL:
    case DESIGNER_WORKFLOWS.OPTIMIZATION_REDIRECT_TEST:
      return true;
    case DESIGNER_WORKFLOWS.CANVAS:
    case DESIGNER_WORKFLOWS.COMMENT:
    case DESIGNER_WORKFLOWS.LOGIC_FLOWS:
    case DESIGNER_WORKFLOWS.ANALYZE_VISUAL_VIEW:
    case DESIGNER_WORKFLOWS.CODE_MODE:
    case DESIGNER_WORKFLOWS.AGENTS:
      return false;
    case DESIGNER_WORKFLOWS.CAMPAIGNS_HOME:
      return true;
    // default:
    // NOTE: The default case is intentionally left out unimplemented so that TS can
    // enforce adding a new explicit case when a new `WorkflowType` is defined.
  }
};

// user types
export const getIsAuthorizedInDesignerUserType: (
  arg1: DesignerStateType
) => boolean = (state) =>
  getDesignerUserType(state) === DESIGNER_USER_TYPES.AUTHORIZED_IN_DESIGNER;
export const getIsUnauthorizedUserType: (arg1: DesignerStateType) => boolean = (
  state
) => getDesignerUserType(state) === DESIGNER_USER_TYPES.UNAUTHORIZED;
export const selectIsReadOnlyMode = (state: DesignerStores) => {
  // Treat anonymous comment like read-only to align with Preview behavior for AI sites
  const isUnauthorized = getIsUnauthorizedUserType(state.DesignerStore);
  const isAnonymousComment = getIsAnonymousCommentUserType(state.DesignerStore);
  return isUnauthorized || isAnonymousComment;
};
export const getIsAnonymousCommentUserType: (
  arg1: DesignerStateType
) => boolean = (state) =>
  getDesignerUserType(state) === DESIGNER_USER_TYPES.ANONYMOUS_COMMENT;
export const selectIsAnonymousCommentMode = (state: DesignerStores) =>
  getIsAnonymousCommentUserType(state.DesignerStore);

export const getIsBranchMode: (arg1: DesignerStateType) => boolean = (state) =>
  state.isPageBranch;

// site modes
export const getIsDesignSiteMode: (arg1: DesignerStateType) => boolean = (
  state
) => getDesignerSiteMode(state) === DESIGNER_SITE_MODES.DESIGN;

export const getIsViewSiteMode: (arg1: DesignerStateType) => boolean = (
  state
) => getDesignerSiteMode(state) === DESIGNER_SITE_MODES.VIEW;

export const selectIsViewSiteMode = (state: DesignerStores) =>
  getDesignerSiteMode(state.DesignerStore) === DESIGNER_SITE_MODES.VIEW;

// edit site mode - includes edit mode & build mode
export const getIsEditSiteMode: (arg1: DesignerStateType) => boolean = (
  state
) => getDesignerSiteMode(state) === DESIGNER_SITE_MODES.EDIT;
// 'sub modes' in the edit site mode - includes edit mode & build mode
// build mode has all the same capabilities as edit mode + additional capabilities
// that are enabled via site permissions
export const selectIsEditMode = (state: DesignerStores) => {
  const isEditSiteMode = getIsEditSiteMode(state.DesignerStore);
  const isBuildMode = selectDesignerAbility('isInPageBuildMode')(state);
  return isEditSiteMode && !isBuildMode;
};
export const selectIsBuildMode = (state: DesignerStores) =>
  selectDesignerAbility('isInPageBuildMode')(state);

/**
 * Whether on-canvas spacing / sizing controls may be shown for the current site mode.
 * Allowed only in Design site mode. Not in View (marketer), Edit-site page build
 * mode, or Edit-site content editing (editor without page build).
 */
export const selectCanShowOnCanvasSizingSpacingControls = (
  state: DesignerStores
) => getIsDesignSiteMode(state.DesignerStore);

export const getIsApplicationLoaded: (arg1: DesignerStores) => boolean = (
  state
) => state.ExternalStore.applicationLoaded;

export const selectFontVariablesFamily = createSelector(
  (state: DesignerStores) => state.DesignerStore.fonts,
  (allFonts) =>
    allFonts.reduce<Record<string, any>>((fonts, font) => {
      if (font.axes && font.axes.length > 0) {
        fonts[font.family] = {
          label: font.family,
          axes: font.axes.reduce<Record<string, any>>((axesMap, axis) => {
            axesMap[axis.tag] = axis;
            return axesMap;
          }, {}),
        };
      }

      return fonts;
    }, {})
);

export const selectShouldShowStyleTab: (arg1: DesignerStores) => boolean = (
  state
) => {
  const element = selectSelectedElement(state);

  return !(element && isOptimizationElement(element));
};

export const selectShouldShowInteractionsTab: (
  arg1: DesignerStores
) => boolean = (state) => {
  const element = selectSelectedElement(state);

  return !(element && isOptimizationElement(element));
};

/**
 * Returns the label for the selected component instance.
 * If the selected element is not a component instance, returns null.
 */
export const selectSelectedComponentInstanceLabel: (arg1: {
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
}) => string | null = createSelector(
  selectElementFromSelectedNodeNativeId,
  selectComponents,
  selectCurrentComponent,
  hasSelectedComponentInstance,
  (state: {DesignerStore: DesignerStateType}) => state.DesignerStore.plugins,
  (
    element,
    liveComponentMap,
    currentComponent,
    isComponentInstance,
    plugins
  ) => {
    if (!element || !isComponentInstance) {
      return null;
    }
    return getElementLabel(
      element,
      Component.getRender(currentComponent),
      liveComponentMap,
      plugins
    );
  }
);

export const selectBreakpoints = ({StyleBlockStore}: DesignerStores) =>
  StyleBlockStore.breakpoints;

export const selectActiveBreakpoint = ({
  ExternalStore,
  StyleBlockStore,
}: DesignerStores): Breakpoint | undefined =>
  StyleBlockStore.breakpoints[ExternalStore.mediaQuery];

export const selectIsPageBranch = (state: DesignerStores): boolean =>
  Boolean(state.DesignerStore.isPageBranch);

export const selectIsPreviewMode = (state: {
  DesignerStore: DesignerStateType;
}): boolean => getIsPreviewWorkflow(state.DesignerStore);

export const selectIsCommentMode = (state: DesignerStores): boolean =>
  getIsAnnotationsWorkflow(state.DesignerStore);

export const selectIsAnalyzeMode = (state: DesignerStores): boolean =>
  getIsAnalyzeWorkflow(state.DesignerStore);

export const selectIsCampaignReportingOpen = (state: DesignerStores): boolean =>
  state.UiStore.isCampaignReportingOpen ?? false;

export const selectIsRulerPinned = createSelector(
  selectCanvasRulerState,
  (rulerState) =>
    rulerState === 'ruler-pinned' || rulerState === 'ruler-pinned-crosshair'
);

export const selectShouldDisplayRulerInCanvasBar = createSelector(
  (state: DesignerStores) => state.UiStore.canvasBarRulerVisible,
  selectActiveLeftPanel,
  selectIsRulerPinned,
  (forceDisplayRulerInCanvas, isLeftPanelActive, isRulerPinned) => {
    // We will never display the ruler if any of the panels in the left sidebar
    // are enabled. We do this so that neither ruler or cross hair overlaps
    // with any of the panels. It does not apply to docked panels.
    if (isLeftPanelActive) {
      return false;
    }

    // We will always display the ruler if the user has chosen this as a setting,
    // or if we need to force it for some reason (e.g. the canvas is being resized).
    return forceDisplayRulerInCanvas || isRulerPinned;
  }
);

export const selectShouldShowCanvasBar = createSelector(
  ({UiStore}: DesignerStores) => UiStore.uiHidden,
  selectIsPreviewMode,
  (isuiHidden, isPreviewMode) => !isuiHidden || !isPreviewMode
);

export const selectSelectionSetSize = createSelector(
  ({UiNodeStore}: DesignerStores) => UiNodeStore.selectionSet,
  (selectionSet) => {
    return selectionSet.size;
  }
);

export const isInOptimizeModeSelector = (state: DesignerStores) =>
  selectIsInOptimizeMode(state.OptimizationStore);

export const selectIsViewingBaseOptimization = (state: DesignerStores) =>
  selectCurrentVariationIsBase(state.OptimizationStore);

export const currentOptimizationVariationIdSelector = (state: DesignerStores) =>
  selectCurrentVariationId(state.OptimizationStore);

export const currentExperienceIdSelector = (state: DesignerStores) =>
  selectCurrentExperienceId(state.OptimizationStore);

/**
 * Resolves the appearance theme that Designer chrome and APIs should show for
 * the current site surface — preview, then surface override, then global
 * (with implicit System when nothing is persisted).
 */
export function resolveAppearanceSelectedOption(
  appearanceSettingsStore: {
    selectedOption: AppearanceSettings;
    globalThemeSelection?: GlobalThemeSelection;
    darkThemeStyle?: DarkThemeStyle;
    osThemePreference?: OSThemePreference;
    themeSelectionPreview?: GlobalThemeSelection;
    themeSurfaceOverrides?: ThemeSurfaceOverrides;
  },
  siteKind: string | null | undefined,
  isGlobalInterfaceSettingsEnabled = false
): AppearanceSettings {
  return resolveAppearanceThemeForSurface({
    ...appearanceSettingsStore,
    isGlobalInterfaceSettingsEnabled,
    siteKind,
  });
}

const selectGlobalInterfaceSettingsEnabled =
  selectDesignerAbility('isGlobalInterfaceSettingsEnabled') ?? (() => false);

export const selectAppearanceSettingsSelectedOption = createSelector(
  (state: DesignerStores) => state.AppearanceSettingsStore,
  (state: DesignerStores) => state.SiteDataStore?.kind,
  selectGlobalInterfaceSettingsEnabled,
  resolveAppearanceSelectedOption
);

export const getIsOptimizationRedirectTestWorkflow = (
  state: DesignerStateType
) =>
  getDesignerWorkflow(state) === DESIGNER_WORKFLOWS.OPTIMIZATION_REDIRECT_TEST;

export const selectIsOptimizationRedirectTestWorkflow = (
  state: DesignerStores
) => getIsOptimizationRedirectTestWorkflow(state.DesignerStore);

export const selectIsCustomCodePreviewEnabled = (state: DesignerStores) => {
  /**
   * If the user has not explicitly toggled the custom code preview setting, we
   * will use the site level setting (which has been set by a site admin). If the
   * user has explicitly toggled the setting, we will use that value.
   */
  return (
    state.UiStore.isCustomCodePreviewEnabled === 'enabled' ||
    (state.UiStore.isCustomCodePreviewEnabled === 'unknown' &&
      state.UiStore.isSiteCustomCodePreviewEnabled)
  );
};

export const selectIsSiteCustomCodePreviewEnabled = (state: DesignerStores) =>
  state.UiStore.isSiteCustomCodePreviewEnabled;

export const selectIsFirstTimeCustomCodePreview = (state: DesignerStores) =>
  state.UiStore.isFirstTimeCustomCodePreview ?? true;

/**
 * Selects whether custom code is allowed based on the site's plan
 */
export const selectAllowCustomCode = (state: DesignerStores) =>
  state.SiteDataStore.allowCustomCode;

export const selectSiteShortName = (state: DesignerStores) =>
  state.SiteDataStore.shortName;

export const selectSiteId = (state: DesignerStores) =>
  state.SiteDataStore.siteId;

/**
 * Returns whether a shortcut or context menu element rename operation is currently active
 */
const selectIsRenamingActive = (state: DesignerStores): boolean =>
  state.UiNodeStore.renameState !== 'idle';

/**
 * Returns whether the right panel is renaming an element.
 */
export const selectIsRightPanelRenaming = (state: DesignerStores): boolean =>
  selectIsRenamingActive(state) &&
  state.UiNodeStore.renameSource === 'right panel';

/**
 * Returns whether the navigator is renaming an element.
 */
export const selectIsNavigatorRenaming = (state: DesignerStores): boolean =>
  selectIsRenamingActive(state) &&
  state.UiNodeStore.renameSource === 'navigator';

/**
 * Checks if the data manager modal is currently open
 */
export const isDataManagerModalOpen = (state: DesignerStores): boolean =>
  Boolean(state.CurrentCollectionStore.showInDataManagerModal);

export const selectIsCodeComponentEditorVisible = (state: DesignerStores) =>
  !selectIsPreviewMode(state) && selectIsCodeComponentCanvas(state);

/**
 * Determines if the `FocusedSymbolMask` should be rendered.
 */
export const selectIsFocusedSymbolMaskVisible = createSelector(
  OpenCanvas.selectIsFeatureActive('componentCanvas'),
  (state: DesignerStores) => state.UiNodeStore.focusedSymbolNativeIdPath,
  (isComponentCanvasActive, focusedSymbolNativeIdPath): boolean => {
    return isEditingComponentInContext({
      isComponentCanvasActive,
      focusedSymbolNativeIdPath,
    });
  }
);

export const selectCurrentTab = (state: DesignerStores) => state.TabStore.tab;

export const selectHomePage = createSelector(
  (state: DesignerStores) => selectPageState(state).staticPages,
  (staticPages) => {
    const homePage = staticPages.find((page) => page.isHome);
    return homePage ?? null;
  }
);

export const selectComponentInstanceCount = createSelector(
  selectComponents,
  (state: DesignerStores) => state.DesignerStore.componentInstanceCounts,
  selectCurrentPageId,
  selectIsPageBranch,
  selectCurrentUserComponentId,
  OpenCanvas.selectIsFeatureActive('componentCanvas'),
  (
    components,
    componentInstanceCounts,
    currentPageId,
    isPageBranch,
    currentComponentId,
    isComponentCanvasActive
  ) => {
    if (!currentComponentId) return 0;

    if (isPageBranch && currentPageId) {
      // For page branches, only show instances on this specific page, including local edits
      const countsByPage = getComponentInstanceCountsByPage(components)(
        componentInstanceCounts ?? {}
      )(currentPageId);

      return countsByPage[currentComponentId]?.[currentPageId] ?? 0;
    }

    // For regular pages, show total across all pages
    const summary = getComponentInstanceCountsSummary(components)(
      isComponentCanvasActive
    )(componentInstanceCounts);
    return summary[currentComponentId] ?? 0;
  }
);
